import { Component, OnInit } from '@angular/core';
import {
  EventPerBookingAddOn,
  EventPerBookingFee,
  EventPerParticipantAddOn,
  EventPerParticipantFee,
  EventPricingMatrix,
  EventType
} from '../../../../../../../models/event-type';
import { ScheduleType } from '../../../../../../../models/event-type-enums';
import { ActivatedRoute, Router } from '@angular/router';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  EditEventTypePricingMatrixFeeModalComponent
} from './modals/edit-event-type-pricing-matrix-fee-modal/edit-event-type-pricing-matrix-fee-modal.component';


@Component({
  selector: 'app-event-type-pricing-matrix-edit',
  templateUrl: './event-type-pricing-matrix-edit.component.html',
  styleUrls: ['./event-type-pricing-matrix-edit.component.css']
})
export class EventTypePricingMatrixEditComponent implements OnInit {
  public eventType: EventType = null;

  public isNewMatrix: boolean;
  public matrix: EventPricingMatrix = {
    id: null,
    url: null,
    name: '',
    per_participant_fees: [],
    per_booking_fees: [],
    per_participant_add_ons: [],
    per_booking_add_ons: [],
    per_booking_deposit: null,
    vouchers_include_entry: false,
    allow_check_in_before_final_payment: false,
    is_removed: false,
    created_at: null,
    updated_at: null
  };

  // Whether the matrix had a per-booking deposit (including $0, which has a
  // real effect) when loaded. Captured once so the field doesn't disappear
  // mid-edit when cleared; see showPerBookingDeposit.
  public matrixHadDepositConfigured = false;

  public eventTypeNotFound = false;
  public matrixNotFound = false;
  public serverError: number = null;
  public errorMessage = '';
  public matrixErrors: any = {};

  public loadingEventType = false;
  public loadingExistingMatrix = false;
  public loadingSubmit = false;

  public readonly ScheduleType = ScheduleType;

  constructor(
    public orgConfig: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private accountService: AccountService,
    private eventTypeService: EventTypeService
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;

    const eventTypeIdInt = parseInt(routeParams.eventTypeId, 10);
    if (isNaN(eventTypeIdInt)) {
      this.eventTypeNotFound = true;
      return;
    }
    this.loadEventType(eventTypeIdInt);

    this.isNewMatrix = !routeParams.hasOwnProperty('id');

    // Get the pricing matrix to edit, if an ID was provided.
    if (!this.isNewMatrix) {
      const matrixIdInt = parseInt(routeParams.id, 10);
      if (isNaN(matrixIdInt)) {
        this.matrixNotFound = true;
        return;
      }

      this.loadMatrix(eventTypeIdInt, matrixIdInt);
    }
  }

  /**
   * Whether the Per-Booking Deposit field should be shown for this matrix.
   *
   * For private event request forms, the entire pricing matrix acts as a
   * deposit for the future event, so a separate deposit amount is not useful
   * and is hidden. The exception is a matrix that already has a deposit
   * configured (legacy data, or the request-form setting being turned on after
   * the deposit was set) — there we keep showing the field so staff can still
   * view or remove the existing deposit.
   */
  public get showPerBookingDeposit(): boolean {
    if (!this.eventType || !this.eventType.is_private_event_request_form) {
      return true;
    }
    return this.matrixHadDepositConfigured;
  }

  /**
   * Whether to warn that a deposit of 0 collects nothing at booking time.
   *
   * A zero deposit is NOT equivalent to an empty deposit: an empty deposit
   * means the full amount is due at booking, whereas a deposit of 0 means $0 is
   * collected at booking and the entire balance is deferred (see
   * Invoice.deposit_amount_due on the backend).
   */
  public get showZeroDepositWarning(): boolean {
    const deposit = this.matrix.per_booking_deposit;
    return this.depositValueIsPresent(deposit) && Number(deposit) === 0;
  }

  /**
   * Whether to suggest removing the deposit on a private event request form,
   * where the pricing matrix itself already acts as the deposit for the future
   * event, so a separate per-booking deposit is usually unnecessary.
   */
  public get showRequestFormDepositWarning(): boolean {
    return (
      !!this.eventType
      && this.eventType.is_private_event_request_form
      && this.depositValueIsPresent(this.matrix.per_booking_deposit)
    );
  }

  private depositValueIsPresent(deposit: string | null | undefined): boolean {
    return deposit !== null && deposit !== undefined && deposit !== '';
  }

  public editPerParticipantFee(fee: EventPerParticipantFee) {
    const modalRef = this.modalService.open(EditEventTypePricingMatrixFeeModalComponent);
    modalRef.componentInstance.inputFee = fee;
    modalRef.componentInstance.useIncludesEntryField = true;
    modalRef.componentInstance.useSellVoucherViaPOSField = true;
    modalRef.componentInstance.usePerBookingFields = false;
    modalRef.componentInstance.headerText = 'Edit Per-Participant Fee';
    modalRef.componentInstance.completedCallback = (result: any) => {
      fee.name = result.name;
      fee.fee = result.fee;
      fee.includes_entry = result.includes_entry;
      fee.sell_voucher_via_pos = result.sell_voucher_via_pos;
    };
  }

  public addPerParticipantFee() {
    const modalRef = this.modalService.open(EditEventTypePricingMatrixFeeModalComponent);
    modalRef.componentInstance.useIncludesEntryField = true;
    modalRef.componentInstance.useSellVoucherViaPOSField = true;
    modalRef.componentInstance.usePerBookingFields = false;
    modalRef.componentInstance.headerText = 'Add Per-Participant Fee';
    modalRef.componentInstance.completedCallback = (result: any) => {
      this.matrix.per_participant_fees.push({
        name: result.name,
        fee: result.fee,
        includes_entry: result.includes_entry,
        sell_voucher_via_pos: result.sell_voucher_via_pos,
        revenue_account_codes: {}
      });
    };
  }

  public editPerBookingFee(fee: EventPerBookingFee) {
    const modalRef = this.modalService.open(EditEventTypePricingMatrixFeeModalComponent);
    modalRef.componentInstance.inputFee = fee;
    modalRef.componentInstance.useIncludesEntryField = true;
    modalRef.componentInstance.useSellVoucherViaPOSField = true;
    modalRef.componentInstance.usePerBookingFields = true;
    modalRef.componentInstance.headerText = 'Edit Per-Booking Fee';
    modalRef.componentInstance.completedCallback = (result: any) => {
      fee.name = result.name;
      fee.fee = result.fee;
      fee.includes_entry = result.includes_entry;
      fee.sell_voucher_via_pos = result.sell_voucher_via_pos;
      fee.included_participants = result.included_participants;
      fee.additional_participant_fee = result.additional_participant_fee;
    };
  }

  public addPerBookingFee() {
    const modalRef = this.modalService.open(EditEventTypePricingMatrixFeeModalComponent);
    modalRef.componentInstance.useIncludesEntryField = true;
    modalRef.componentInstance.useSellVoucherViaPOSField = true;
    modalRef.componentInstance.usePerBookingFields = true;
    modalRef.componentInstance.headerText = 'Add Per-Booking Fee';
    modalRef.componentInstance.completedCallback = (result: any) => {
      this.matrix.per_booking_fees.push({
        name: result.name,
        fee: result.fee,
        includes_entry: result.includes_entry,
        sell_voucher_via_pos: result.sell_voucher_via_pos,
        included_participants: result.included_participants,
        additional_participant_fee: result.additional_participant_fee,
        revenue_account_codes: {}
      });
    };
  }

  public editPerParticipantAddOn(addOn: EventPerParticipantAddOn) {
    const modalRef = this.modalService.open(EditEventTypePricingMatrixFeeModalComponent);
    modalRef.componentInstance.inputFee = addOn;
    modalRef.componentInstance.useIncludesEntryField = false;
    modalRef.componentInstance.useSellVoucherViaPOSField = false;
    modalRef.componentInstance.usePerBookingFields = false;
    modalRef.componentInstance.useAllowMultipleCopiesField = true;
    modalRef.componentInstance.isPerParticipantAddOn = true;
    modalRef.componentInstance.headerText = 'Edit Per-Participant Add-On';
    modalRef.componentInstance.completedCallback = (result: any) => {
      addOn.name = result.name;
      addOn.fee = result.fee;
      addOn.allow_multiple_copies = result.allow_multiple_copies;
      addOn.max_copies = result.max_copies;
      addOn.multiple_copies_input_type = result.multiple_copies_input_type;
    };
  }

  public addPerParticipantAddOn() {
    const modalRef = this.modalService.open(EditEventTypePricingMatrixFeeModalComponent);
    modalRef.componentInstance.useIncludesEntryField = false;
    modalRef.componentInstance.useSellVoucherViaPOSField = false;
    modalRef.componentInstance.usePerBookingFields = false;
    modalRef.componentInstance.useAllowMultipleCopiesField = true;
    modalRef.componentInstance.isPerParticipantAddOn = true;
    modalRef.componentInstance.headerText = 'Add Per-Participant Add-On';
    modalRef.componentInstance.completedCallback = (result: any) => {
      this.matrix.per_participant_add_ons.push({
        name: result.name,
        fee: result.fee,
        revenue_account_codes: {},
        allow_multiple_copies: result.allow_multiple_copies,
        max_copies: result.max_copies,
        multiple_copies_input_type: result.multiple_copies_input_type,
      });
    };
  }

  public editPerBookingAddOn(addOn: EventPerBookingAddOn) {
    const modalRef = this.modalService.open(EditEventTypePricingMatrixFeeModalComponent);
    modalRef.componentInstance.inputFee = addOn;
    modalRef.componentInstance.useIncludesEntryField = false;
    modalRef.componentInstance.useSellVoucherViaPOSField = false;
    modalRef.componentInstance.usePerBookingFields = true;
    modalRef.componentInstance.useAllowMultipleCopiesField = true;
    modalRef.componentInstance.isPerParticipantAddOn = false;
    modalRef.componentInstance.headerText = 'Edit Per-Booking Add-On';
    modalRef.componentInstance.completedCallback = (result: any) => {
      addOn.name = result.name;
      addOn.fee = result.fee;
      addOn.included_participants = result.included_participants;
      addOn.additional_participant_fee = result.additional_participant_fee;
      addOn.allow_multiple_copies = result.allow_multiple_copies;
      addOn.max_copies = result.max_copies;
      addOn.multiple_copies_input_type = result.multiple_copies_input_type;
    };
  }

  public addPerBookingAddOn() {
    const modalRef = this.modalService.open(EditEventTypePricingMatrixFeeModalComponent);
    modalRef.componentInstance.useIncludesEntryField = false;
    modalRef.componentInstance.useSellVoucherViaPOSField = false;
    modalRef.componentInstance.usePerBookingFields = true;
    modalRef.componentInstance.useAllowMultipleCopiesField = true;
    modalRef.componentInstance.isPerParticipantAddOn = false;
    modalRef.componentInstance.headerText = 'Add Per-Booking Add-On';
    modalRef.componentInstance.completedCallback = (result: any) => {
      this.matrix.per_booking_add_ons.push({
        name: result.name,
        fee: result.fee,
        included_participants: result.included_participants,
        additional_participant_fee: result.additional_participant_fee,
        revenue_account_codes: {},
        allow_multiple_copies: result.allow_multiple_copies,
        max_copies: result.max_copies,
        multiple_copies_input_type: result.multiple_copies_input_type,
      });
    };
  }

  public handleSubmit() {
    // This should not be possible, as the save button is not rendered while
    // loading the event type.
    if (!this.eventType) {
      return;
    }

    this.loadingSubmit = true;

    this.errorMessage = '';
    this.matrixErrors = {};

    let matrixSavePromise: Promise<EventPricingMatrix>;
    if (this.isNewMatrix) {
      matrixSavePromise = this.eventTypeService.addEventPricingMatrix(this.eventType.id, this.matrix);
    } else {
      matrixSavePromise = this.eventTypeService.updateEventPricingMatrix(this.matrix);
    }

    matrixSavePromise
      .then(matrix => {
        let successMessage: string;
        if (this.isNewMatrix) {
          successMessage = matrix.name + ' created successfully.';
        } else {
          successMessage = matrix.name + ' updated successfully.';
        }

        this.router.navigate(
          ['/events/setup/edit/' + this.eventType.id.toString() + '/pricing-matrices'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.matrixErrors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewMatrix) {
            this.errorMessage = 'You do not have permission to add pricing matrices.';
          } else {
            this.errorMessage = 'You do not have permission to modify this pricing matrix.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  private loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService.getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  private loadMatrix(eventTypeId: number, matrixId: number) {
    this.loadingExistingMatrix = true;

    this.eventTypeService.getEventPricingMatrix(eventTypeId, matrixId)
      .then(matrix => {
        this.matrix = matrix;
        this.matrixHadDepositConfigured = this.depositValueIsPresent(matrix.per_booking_deposit);
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.matrixNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingMatrix = false; });
  }
}
